import React, { createContext, useContext, useMemo, useCallback } from 'react';

// 기본 거래대금 표 설정
const defaultTableSettings = {
  stockNameFilter: {
    enabled: false,
    mode: 'exclude',
    keywords: [],
    caseSensitive: false
  },
  themeFilter: {
    enabled: false,
    mode: 'exclude',
    keywords: [],
    caseSensitive: false
  },
  visibleColumns: {
    rank: true,
    name: true,
    price: true,
    rate: true,
    tradingValue: true,
    volume: true,
    marketCap: true,
    theme: true
  },
  themeDisplay: {
    showAll: false,
    maxThemes: 3
  },
  colorScheme: {
    positiveRate: '#d32f2f',
    negativeRate: '#1976d2',
    neutralRate: '#757575'
  }
};

// 서버 설정 목록
const defaultServers = [
  {
    id: 'stock-api-server',
    name: '주식 거래대금 API 서버',
    baseUrl: 'http://localhost:8003',
    domain: 'localhost',
    apiTimeout: 10000,
    isActive: true
  },
  {
    id: 'board-api-server',
    name: '게시판 API 서버',
    baseUrl: 'http://localhost:8002',
    domain: 'localhost',
    apiTimeout: 10000,
    isActive: true,
    description: '게시판, 게시글, 댓글 관리 API 서버'
  },
  {
    id: 'auth-api-server',
    name: '인증 API 서버',
    baseUrl: 'http://localhost:8001',
    domain: 'localhost',
    apiTimeout: 10000,
    isActive: true,
    description: '로그인, 회원가입, 사용자 관리 API 서버'
  },
  {
    id: 'chart-api-server',
    name: '차트 API 서버',
    baseUrl: 'http://localhost:8004',
    domain: 'localhost',
    apiTimeout: 15000,
    isActive: true
  },
  {
    id: 'alert-api-server',
    name: '알고리즘 분석 API 서버',
    baseUrl: 'http://localhost:7001',
    domain: 'localhost',
    apiTimeout: 59000,
    isActive: true
  },
  {
    id: 'stock-report-api-server',
    name: '증권리포트 API 서버',
    baseUrl: 'http://localhost:8005',
    domain: 'localhost',
    apiTimeout: 10000,
    isActive: true,
    description: 'PDF 증권리포트 조회 및 통계 분석 API 서버'
  },
  {
    id: 'stock-db-api-server',
    name: '종목 DB API 서버',
    baseUrl: 'http://localhost:8006',
    domain: 'localhost',
    apiTimeout: 10000,
    isActive: true,
    description: '종목 정보, 테마, 현재가 조회 API 서버'
  },
  {
    id: 'naver-finance-api-server',
    name: '네이버 금융 API 서버',
    baseUrl: 'http://localhost:8008',
    domain: 'localhost',
    apiTimeout: 30000,
    isActive: true,
    description: '네이버 금융 업종/테마 시세 조회 API 서버'
  },
  {
    id: 'stock-study-api-server',
    name: '스터디 API 서버',
    baseUrl: 'http://localhost:8009',
    domain: 'localhost',
    apiTimeout: 10000,
    isActive: true,
    description: '주식 스터디 템플릿/기록/댓글 관리 API 서버'
  }
];

// Context 생성
export const ServerConfigContext = createContext();

// Provider 컴포넌트
export const ServerConfigProvider = ({ children }) => {
  // 메뉴 권한은 항상 DEFAULT_MENU_PERMISSIONS(코드)에서만 읽음 - localStorage/파일 미사용

  // 특정 서버 조회 - useCallback으로 메모이제이션
  const getServer = useCallback((serverId) => {
    return defaultServers.find(server => server.id === serverId);
  }, []);

  // 활성 서버 목록 조회 - useCallback으로 메모이제이션
  const getActiveServers = useCallback(() => {
    return defaultServers.filter(server => server.isActive);
  }, []);

  // API 엔드포인트 생성 헬퍼 함수 - useCallback으로 메모이제이션
  const getApiUrl = useCallback((serverId, endpoint) => {
    const server = defaultServers.find(s => s.id === serverId);
    if (!server) {
      throw new Error(`서버를 찾을 수 없습니다: ${serverId}`);
    }

    // 프록시 경로 생성 (stock-api, chart-api 등)
    const simplifiedId = serverId.replace('-server', '');
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;

    return `/${simplifiedId}/api/v1${cleanEndpoint}`;
  }, []);

  // 스마트 API URL 생성 함수 - useCallback으로 메모이제이션
  const getSmartApiUrl = useCallback((endpoint) => {
    let serverId;

    if (endpoint.startsWith('/auth/')) {
      serverId = 'auth-api-server';
    } else if (endpoint.startsWith('/api/board/') || endpoint.startsWith('/board/')) {
      serverId = 'board-api-server';
    } else {
      serverId = 'board-api-server';
    }

    const server = defaultServers.find(s => s.id === serverId);
    if (!server) {
      throw new Error(`서버를 찾을 수 없습니다: ${serverId}`);
    }

    const simplifiedId = serverId.replace('-server', '');
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;

    return `/${simplifiedId}/api/v1${cleanEndpoint}`;
  }, []);

  // 주식 데이터 필터링 함수 - useCallback으로 메모이제이션
  const filterStockData = useCallback((data) => {
    if (!Array.isArray(data)) return data;

    let filtered = [...data];

    // 종목명 필터
    if (defaultTableSettings.stockNameFilter.enabled && defaultTableSettings.stockNameFilter.keywords.length > 0) {
      const { mode, keywords, caseSensitive } = defaultTableSettings.stockNameFilter;

      filtered = filtered.filter(stock => {
        const stockName = caseSensitive ? stock.name : stock.name?.toLowerCase();
        const hasMatch = keywords.some(keyword => {
          const searchKeyword = caseSensitive ? keyword : keyword.toLowerCase();
          return stockName?.includes(searchKeyword);
        });

        return mode === 'include' ? hasMatch : !hasMatch;
      });
    }

    // 테마 필터
    if (defaultTableSettings.themeFilter.enabled && defaultTableSettings.themeFilter.keywords.length > 0) {
      const { mode, keywords, caseSensitive } = defaultTableSettings.themeFilter;

      filtered = filtered.filter(stock => {
        if (!stock.themes || !Array.isArray(stock.themes)) return mode === 'exclude';

        const hasMatch = stock.themes.some(theme => {
          const themeStr = caseSensitive ? theme : theme?.toLowerCase();
          return keywords.some(keyword => {
            const searchKeyword = caseSensitive ? keyword : keyword.toLowerCase();
            return themeStr?.includes(searchKeyword);
          });
        });

        return mode === 'include' ? hasMatch : !hasMatch;
      });
    }

    return filtered;
  }, []);

  // Context 값
  const contextValue = useMemo(() => ({
    servers: defaultServers,
    getServer,
    getActiveServers,
    getApiUrl,
    getSmartApiUrl,
    hasActiveServers: defaultServers.some(s => s.isActive),
    isConfigLoaded: true,
    tableSettings: defaultTableSettings,
    filterStockData,
    config: {
      servers: defaultServers,
      menuPermissions: null  // 항상 null → getMenuSettings가 DEFAULT_MENU_PERMISSIONS 사용
    }
  }), [getServer, getActiveServers, getApiUrl, getSmartApiUrl, filterStockData]);

  return (
    <ServerConfigContext.Provider value={contextValue}>
      {children}
    </ServerConfigContext.Provider>
  );
};

// useServerConfig 커스텀 훅
export const useServerConfig = () => {
  const context = useContext(ServerConfigContext);
  if (!context) {
    throw new Error('useServerConfig must be used within a ServerConfigProvider');
  }
  return context;
};
